import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import type { CalendarApi, EventApi } from "@fullcalendar/core/index.js"
import { Draggable } from "@fullcalendar/interaction"
import { ErrorMessage } from "@hookform/error-message"
import {
  CirclePlus,
  Grip,
  MinusCircle,
  Pencil,
  Loader2,
  Send,
} from "lucide-react"
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Combobox } from "@/components/ui/combobox"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { ErrorHint } from "@/components/ui/errorhint"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { PageBody } from "@/components/ui/layout"
import { SelectInput } from "@/components/ui/select"
import { useFullCalendarApi } from "@/lib/fullCalendar"
import {
  CustomClassId,
  TimetablePriorityId,
  TimetableSessionId,
  addTimetablePriority,
  adjustTimetablePriority,
  createPageStore,
  dropTimetableSession,
  loadTimetable,
  removeTimetablePriority,
  removeTimetableSession,
  selectCalendar,
  selectCustomClassOptions,
  selectEventColours,
  selectEventTitle,
  selectFullCalendarEvents,
  selectOrderedTimetableSessionIds,
  selectPriorityIndex,
  selectSessionTemporalInformation,
  selectTimetablePriority,
  selectTimetablePriorityIds,
  selectTimetableSession,
  selectTimetableSessionSchedule,
  selectTimetableSessionsById,
  updateTimetableTimezone,
  updateTimetableTimezoneOffset,
  usePageDispatch,
  usePageSelector,
  PageReduxContext,
} from "./show-reducer"
import { Timetable } from "@/types/teachers/teacherTimetable"
import { Switch } from "@/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { Separator } from "@/components/ui/separator"
import { useScroller } from "@/components/shared/scroller"
import { displayDay, displayPlainTime } from "@/lib/timetableZonedDatetime"
import { AdjustableRanking } from "@/components/shared/adjustable-ranking"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { ActionButton } from "@/components/shared/action-buttons"
import { Provider } from "react-redux"
import { CycleCalendar } from "@/components/shared/cycle-calendar"
import { useNavigate } from "@/lib/page-rendering"

const FullCalendarApiContext = createContext<{
  calendarApi: CalendarApi | null
}>({
  calendarApi: null,
})

export default function PageWithStore(
  props: React.ComponentProps<typeof Page>,
) {
  const [pageStore] = useState(createPageStore())
  return (
    <Provider store={pageStore} context={PageReduxContext}>
      <Page {...props} />
    </Provider>
  )
}

function Page({ timetable }: { timetable?: Timetable }) {
  const [calendarRef, calendarApi] = useFullCalendarApi(
    "TeacherTimetableEventList",
  )
  const { t } = useTranslation(["timetables"])
  const dispatch = usePageDispatch()

  const timetablePriorityIds = usePageSelector(selectTimetablePriorityIds)
  const timetableSessionsById = usePageSelector(selectTimetableSessionsById)
  const { cycleLength, timezone, startTemporalInstant } =
    usePageSelector(selectCalendar)

  const events = usePageSelector((state) =>
    selectFullCalendarEvents(state, { tFunction: t }),
  )

  useEffect(() => {
    timetable && dispatch(loadTimetable(timetable))
  }, [dispatch, timetable])

  if (!cycleLength) {
    return null
  }

  const onCustomClassSelect = (customClassId: CustomClassId) => {
    dispatch(addTimetablePriority({ customClassId }, t))
  }

  const onEventChange = ({
    event,
    revert,
  }: {
    event: EventApi
    revert: () => void
  }) => {
    dispatch(
      dropTimetableSession(t, revert, {
        startStr: event.startStr,
        timetableSessionId: event.extendedProps.timetableSessionId,
      }),
    )
  }

  return (
    <FullCalendarApiContext.Provider value={{ calendarApi }}>
      <EducatorPageChrome action={<PublishTimetableDialog />} />
      <PageBody>
        <Card className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-row p-4">
            <div
              data-testid="priority-list"
              className="flex min-h-0 w-96 flex-col gap-4 overflow-y-auto scroll-smooth"
            >
              <div className="sticky top-0 flex flex-col gap-4 bg-white">
                <SelectCustomClassDialog
                  onSelectCustomClass={onCustomClassSelect}
                />
                <TimezoneSelector />
                <Separator />
              </div>
              {timetablePriorityIds.map((timetablePriorityId) => (
                <PriorityItem
                  key={timetablePriorityId}
                  onCustomClassSelect={onCustomClassSelect}
                  timetablePriorityId={timetablePriorityId}
                />
              ))}
            </div>
            <div
              className="flex min-h-0 flex-1 flex-col p-4"
              style={
                {
                  "--fc-today-bg-color": "transparent",
                } as React.CSSProperties
              }
            >
              <CycleCalendar
                events={events}
                interactive={true}
                cycleLength={cycleLength}
                eventReceive={onEventChange}
                eventDrop={onEventChange}
                calendarRef={calendarRef}
                calendarTimezoneId={timezone}
                calendarStartInstant={startTemporalInstant}
                eventContent={({
                  event: {
                    title,
                    extendedProps: { timetableSessionId },
                  },
                }) => (
                  <>
                    {timetableSessionsById[timetableSessionId] && (
                      <Event
                        timetableSessionId={timetableSessionId}
                        title={title}
                      />
                    )}
                  </>
                )}
              />
            </div>
          </div>
        </Card>
      </PageBody>
    </FullCalendarApiContext.Provider>
  )
}

function PublishTimetableDialog() {
  const navigate = useNavigate()
  const { t } = useTranslation(["timetables", "common"])
  const publishPreferences = () => {
    setPublicationState("pending")
    navigate({
      href: "./timetable_publications",
      method: "post",
      preserveScroll: true,
    }).then(({ errors }) =>
      setPublicationState(
        Object.keys(errors).length === 0 ? "success" : "error",
      ),
    )
  }
  const [publicationState, setPublicationState] = useState<
    "initial" | "pending" | "success" | "error"
  >("initial")
  const [open, setOpen] = useState(false)

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <ActionButton Icon={Send} variant="solid">
          {t("timetables:edit.actions.publish")}
        </ActionButton>
      </AlertDialogTrigger>
      <AlertDialogContent
        onEscapeKeyDown={(e) => {
          if (publicationState !== "pending") return
          e.preventDefault()
        }}
      >
        {publicationState === "initial" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("common:confirmation_dialog.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("timetables:edit.actions.publish_description")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("common:actions.cancel")}
              </AlertDialogCancel>
              <Button onClick={publishPreferences}>
                {t("common:actions.confirm")}
              </Button>
            </AlertDialogFooter>
          </>
        )}
        {publicationState === "pending" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("common:confirmation_dialog.title")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                <div className="flex flex-row items-center gap-2">
                  <Loader2 className="animate-spin" />
                  {t("timetables:edit.actions.publish_pending")}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
          </>
        )}
        {publicationState === "success" && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("common:actions.done")}</AlertDialogTitle>
              <AlertDialogDescription data-testid="publish-preferences-success">
                {t("timetables:edit.actions.publish_success")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                onClick={() => {
                  setPublicationState("initial")
                  setOpen(false)
                }}
              >
                {t("common:actions.close")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  )
}

function PriorityItem({
  timetablePriorityId,
  onCustomClassSelect,
}: {
  timetablePriorityId: TimetablePriorityId
  onCustomClassSelect: (customClassId: CustomClassId) => void
}) {
  const { t } = useTranslation(["timetables", "common"])
  const dispatch = usePageDispatch()
  const { customClass } = usePageSelector((state) =>
    selectTimetablePriority(state, { timetablePriorityId }),
  )
  const customClassId = CustomClassId({ customClassId: customClass.id })
  const priorityIndex = usePageSelector((state) =>
    selectPriorityIndex(state, { timetablePriorityId }),
  )
  const orderedSessionIds = usePageSelector((state) =>
    selectOrderedTimetableSessionIds(state, { timetablePriorityId }),
  )
  const eventTitle = usePageSelector((state) =>
    selectEventTitle(state, {
      timetablePriorityId,
      tFunction: t,
      customClassId,
    }),
  )
  const { cardBorderColour, bgColour, textColour } = usePageSelector((state) =>
    selectEventColours(state, { customClassId, timetablePriorityId }),
  )
  const { setScrollNode, scroll } = useScroller()
  return (
    <div
      data-testid="priority-item"
      className={`flex max-w-fit flex-col items-center gap-2 rounded-md border border-l-4 border-gray-200 p-4 shadow-md ${cardBorderColour}`}
      ref={setScrollNode}
    >
      <div className="flex w-full flex-row items-center justify-between">
        <div
          className={`items-left flex flex-row gap-2 rounded px-2 py-1 text-xs ${bgColour} ${textColour}`}
        >
          {eventTitle}
        </div>
        <Button
          size={"sm"}
          variant={"secondary"}
          className="h-[inherit] w-min px-2 py-1"
          onClick={() => {
            onCustomClassSelect(customClassId)
            scroll()
          }}
        >
          {t("edit.add_priority_below")}
        </Button>
      </div>
      <div className="flex flex-row justify-between">
        <h3 className="truncate text-wrap">
          <span className="font-semibold">
            {t("timetables:edit.schedule.priority", {
              priority: priorityIndex + 1,
            })}
          </span>{" "}
          - {customClass.name}
        </h3>
        <PriorityRanking timetablePriorityId={timetablePriorityId} />
        <Button
          variant={"link-destructive"}
          onClick={() =>
            dispatch(removeTimetablePriority({ timetablePriorityId }, t))
          }
        >
          <MinusCircle /> {t("common:actions.remove")}
        </Button>
      </div>
      {orderedSessionIds.map((timetableSessionId) => (
        <Session key={timetableSessionId} sessionId={timetableSessionId} />
      ))}
    </div>
  )
}

type AvailabilityValues = {
  start_time: string
  day: string[number]
}

function SetClassDialog({
  timetableSessionId,
  dialogTrigger,
}: {
  timetableSessionId: TimetableSessionId
  dialogTrigger: ReactNode
}) {
  const { customClass } = usePageSelector((state) =>
    selectTimetableSession(state, { timetableSessionId }),
  )
  const sessionTemporalDetails = usePageSelector((state) =>
    selectSessionTemporalInformation(state, {
      timetableSessionId,
    }),
  )
  const { t } = useTranslation(["timetables", "common"])
  const daysOfWeek = Array.from({ length: customClass.cycleLength }, (_, i) =>
    t(`timetables:edit.schedule.form.day`, { day: i + 1 }),
  )

  const dispatch = usePageDispatch()
  const { startTemporalZonedDateTime } = usePageSelector(selectCalendar)
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<AvailabilityValues>({
    values: sessionTemporalDetails
      ? {
          start_time:
            sessionTemporalDetails.calendarTimezoneStartPlainTime.toString(),
          day: daysOfWeek[sessionTemporalDetails.calendarTimezoneDay],
        }
      : undefined,
  })
  const [open, setOpen] = useState(false)
  const onSubmit = (data: AvailabilityValues) => {
    const startDate = startTemporalZonedDateTime
      .toPlainDate()
      .add({ days: daysOfWeek.indexOf(data.day) })

    const startPlainTime = Temporal.PlainTime.from(data.start_time)

    const startStr = startDate
      .toPlainDateTime()
      .withPlainTime(startPlainTime)
      .toZonedDateTime("UTC")
      .toInstant()
      .toString()

    dispatch(
      dropTimetableSession(t, () => {}, {
        startStr: startStr,
        timetableSessionId,
      }),
    )
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{dialogTrigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("timetables:edit.schedule.dialog.header")}
          </DialogTitle>
          <DialogDescription>
            {t("timetables:edit.schedule.dialog.description")}
          </DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col">
            <Label
              htmlFor="day"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t("timetables:edit.schedule.form.day_label")}
            </Label>
            <Controller
              control={control}
              name="day"
              rules={{ required: t("common:errors.required") }}
              render={({ field }) => (
                <SelectInput
                  id="day"
                  className="w-full"
                  value={field.value?.toString()}
                  onChange={field.onChange}
                  options={daysOfWeek.map((day) => ({
                    value: day,
                    label: day,
                  }))}
                />
              )}
            />
            <ErrorMessage name="day" errors={errors} render={ErrorHint} />
          </div>
          <div className="flex flex-col">
            <Label
              htmlFor="start_time"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              {t("timetables:edit.schedule.form.start_time")}
            </Label>
            <Input
              className="w-full"
              type="time"
              id={"start_time"}
              role="input"
              {...register(`start_time`, {
                required: t("common:errors.required"),
              })}
            />
            <ErrorMessage
              name="start_time"
              errors={errors}
              render={ErrorHint}
            />
          </div>
          <div className="mt-2 flex w-full flex-col gap-2 pb-4">
            {sessionTemporalDetails ? (
              <Button
                variant={"destructive-outline"}
                className="w-full"
                onClick={() =>
                  dispatch(removeTimetableSession({ timetableSessionId }, t))
                }
              >
                <MinusCircle /> {t("common:actions.remove")}
              </Button>
            ) : null}
            <Button type="submit" className="w-full">
              {sessionTemporalDetails
                ? t("timetables:edit.schedule.form.submit_edit")
                : t("timetables:edit.schedule.form.submit_add")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function SelectCustomClassDialog({
  onSelectCustomClass,
}: {
  onSelectCustomClass: (customClassId: CustomClassId) => void
}) {
  const { scroll } = useScroller()
  const { t } = useTranslation(["timetables", "common"])
  const [selectedCustomClassId, setSelectedCustomClassId] =
    useState<CustomClassId | null>()
  const customClassOptions = usePageSelector(selectCustomClassOptions)

  const onCloseDialog = useCallback(() => {
    if (!selectedCustomClassId) {
      return
    }
    onSelectCustomClass(selectedCustomClassId!)
    setSelectedCustomClassId(null)
  }, [selectedCustomClassId, onSelectCustomClass])

  return (
    <Dialog>
      <div className="flex w-full flex-col bg-white p-2">
        <DialogTrigger asChild>
          <Button id="add-custom-class">
            {t("timetables:edit.add_custom_class")}
          </Button>
        </DialogTrigger>
      </div>
      <DialogContent onCloseAutoFocus={scroll}>
        <DialogHeader>
          <DialogTitle>{t("timetables:edit.add_custom_class")}</DialogTitle>
          <DialogDescription>
            {t("timetables:edit.add_custom_class_dialog_description")}
          </DialogDescription>
        </DialogHeader>
        <Combobox
          options={customClassOptions}
          value={selectedCustomClassId}
          onChange={setSelectedCustomClassId}
          ariaLabelledBy="add-custom-class"
        />
        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={!selectedCustomClassId} onClick={onCloseDialog}>
              {t("common:actions.add")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function PriorityRanking({
  timetablePriorityId,
}: {
  timetablePriorityId: TimetablePriorityId
}) {
  const dispatch = usePageDispatch()
  const length = usePageSelector(selectTimetablePriorityIds).length
  const { t } = useTranslation(["timetables"])

  const handleAdjustPriority = (direction: "up" | "down") => {
    dispatch(adjustTimetablePriority({ timetablePriorityId, direction }, t))
  }

  const priorityIndex = usePageSelector((state) =>
    selectPriorityIndex(state, { timetablePriorityId }),
  )

  // only item - can't move up or down
  if (length === 1) {
    return null
  }

  // can do both
  return (
    <>
      <AdjustableRanking
        increaseButtonProps={{
          onClick: () => handleAdjustPriority("up"),
          disabled: priorityIndex === 0,
          "aria-label":
            priorityIndex === 0
              ? "Cannot increase priority - already at highest"
              : "Increase priority",
        }}
        decreaseButtonProps={{
          onClick: () => handleAdjustPriority("down"),
          disabled: priorityIndex === length - 1,
          "aria-label":
            priorityIndex === length - 1
              ? "Cannot decrease priority - already at lowest"
              : "Decrease priority",
        }}
      />
    </>
  )
}

function DraggableSession({
  timetableSessionId,
}: {
  timetableSessionId: TimetableSessionId
}) {
  const draggableRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation(["timetables"])
  const { session, customClass, timetablePriorityId } = usePageSelector(
    (state) => selectTimetableSession(state, { timetableSessionId }),
  )
  const customClassId = CustomClassId({ customClassId: customClass.id })
  const { duration } = session
  const eventTitle = usePageSelector((state) =>
    selectEventTitle(state, {
      timetablePriorityId,
      tFunction: t,
      customClassId,
    }),
  )
  const eventColours = usePageSelector((state) =>
    selectEventColours(state, { customClassId, timetablePriorityId }),
  )
  useEffect(() => {
    if (draggableRef.current) {
      const draggable = new Draggable(draggableRef.current, {
        eventData: {
          extendedProps: {
            timetableSessionId,
            colours: eventColours,
          },
          title: eventTitle,
          duration: {
            minutes: duration,
          },
        },
      })

      return () => draggable.destroy()
    }
  })

  return (
    <div
      className="flex w-full cursor-grab flex-row items-center justify-center gap-2 rounded-md bg-gray-100 p-2"
      ref={draggableRef}
    >
      <Grip className="size-3" />
      {t("timetables:edit.schedule.duration", { duration })}
    </div>
  )
}

function TimezoneSelector() {
  const { t } = useTranslation(["timetables", "common"])
  const { timezone } = usePageSelector(selectCalendar)
  const timezones = Intl.supportedValuesOf("timeZone")

  const dispatch = usePageDispatch()

  return (
    <Card className="flex flex-col gap-2 p-2 font-normal">
      <div>
        <Label
          htmlFor="timezone"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          {t("timetables:edit.timezone")}
        </Label>
        <Combobox
          id="timezone"
          value={timezone}
          onChange={(val) => {
            if (val !== null) {
              dispatch(updateTimetableTimezone({ timezone: val }))
            }
          }}
          options={timezones.map((timezone: string) => ({
            label: timezone,
            value: timezone,
          }))}
        />
      </div>
      <DSTSwitch />
    </Card>
  )
}

function Event({
  timetableSessionId,
  title,
}: {
  timetableSessionId: TimetableSessionId
  title: string
}) {
  const { t } = useTranslation(["timetables"])
  const { timetablePriorityId, customClass } = usePageSelector((state) =>
    selectTimetableSession(state, { timetableSessionId }),
  )
  const priorityIndex = usePageSelector((state) =>
    selectPriorityIndex(state, { timetablePriorityId }),
  )
  const sessionTemporalDetails = usePageSelector((state) =>
    selectSessionTemporalInformation(state, {
      timetableSessionId,
    }),
  )

  if (!sessionTemporalDetails) {
    return null
  }

  const {
    duration,
    inCalendarTimezone,
    timezoneAbbreviation,
    calendarTimezoneDay,
    calendarTimezoneEndPlainTime,
    calendarTimezoneStartPlainTime,
    eventDay,
    eventEndPlainTime,
    eventStartPlainTime,
  } = sessionTemporalDetails

  return (
    <div className="fc-event-main-frame">
      <TooltipProvider>
        <Tooltip>
          <SetClassDialog
            timetableSessionId={timetableSessionId}
            dialogTrigger={
              <TooltipTrigger asChild>
                <Button
                  variant="link"
                  className="fc-event-title fc-sticky top-0 m-0 h-fit justify-start p-0 text-xs text-current ring-current!"
                >
                  {title}
                  {!inCalendarTimezone && "*"}
                </Button>
              </TooltipTrigger>
            }
          />
          <TooltipContent>
            {t("timetables:edit.schedule.priority", {
              priority: priorityIndex + 1,
            })}{" "}
            - {customClass.name}
            <br />
            {t("timetables:edit.schedule.duration", { duration })}
            <br />
            {t("timetables:edit.schedule.fixed", {
              day: displayDay(calendarTimezoneDay),
              start: displayPlainTime(calendarTimezoneStartPlainTime),
              end: displayPlainTime(calendarTimezoneEndPlainTime),
            })}
            {!inCalendarTimezone && (
              <>
                <br />
                {t("timetables:edit.schedule.fixed_timezone", {
                  timezone: timezoneAbbreviation,
                  start: displayPlainTime(eventStartPlainTime),
                  end: displayPlainTime(eventEndPlainTime),
                  day: displayDay(eventDay),
                })}
              </>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}

function FixedSessionDetails({
  timetableSessionId,
}: {
  timetableSessionId: TimetableSessionId
}) {
  const { t } = useTranslation(["timetables"])
  const { timezone: calendarTimezoneId } = usePageSelector(selectCalendar)
  const { customClass } = usePageSelector((state) =>
    selectTimetableSession(state, { timetableSessionId }),
  )

  const sessionTemporalDetails = usePageSelector((state) =>
    selectSessionTemporalInformation(state, {
      timetableSessionId,
    }),
  )

  if (!sessionTemporalDetails) {
    return
  }

  const {
    timezoneAbbreviation,
    calendarTimezoneDay,
    calendarTimezoneEndPlainTime,
    calendarTimezoneStartPlainTime,
    eventDay,
    eventEndPlainTime,
    eventStartPlainTime,
  } = sessionTemporalDetails

  const timeToDisplay =
    customClass.timezoneId !== calendarTimezoneId
      ? t("timetables:edit.schedule.fixed_timezone", {
          timezone: timezoneAbbreviation,
          start: displayPlainTime(eventStartPlainTime),
          end: displayPlainTime(eventEndPlainTime),
          day: displayDay(eventDay),
        })
      : null
  return (
    <div className="text-nowrap" data-testid="priority-item-session-time">
      {t("timetables:edit.schedule.fixed", {
        day: displayDay(calendarTimezoneDay),
        start: displayPlainTime(calendarTimezoneStartPlainTime),
        end: displayPlainTime(calendarTimezoneEndPlainTime),
      })}
      <br />
      <span className="text-xs">{timeToDisplay}</span>
    </div>
  )
}

function Session({ sessionId }: { sessionId: TimetableSessionId }) {
  const { t } = useTranslation(["common"])
  const sessionSchedule = usePageSelector((state) =>
    selectTimetableSessionSchedule(state, { timetableSessionId: sessionId }),
  )
  return (
    <div
      data-testid="session-item"
      className="flex w-full flex-row items-center justify-between gap-3"
    >
      {sessionSchedule ? (
        <>
          <FixedSessionDetails timetableSessionId={sessionId} />
          <div className="inline-flex gap-2">
            <SetClassDialog
              timetableSessionId={sessionId}
              dialogTrigger={
                <Button variant={"link"}>
                  <Pencil /> {t("common:actions.edit")}
                </Button>
              }
            />
          </div>
        </>
      ) : (
        <>
          <DraggableSession timetableSessionId={sessionId} />
          <SetClassDialog
            timetableSessionId={sessionId}
            dialogTrigger={
              <Button variant={"link"}>
                <CirclePlus /> {t("common:actions.add")}
              </Button>
            }
          />
        </>
      )}
    </div>
  )
}

function DSTSwitch() {
  const dispatch = usePageDispatch()
  const { timezone, startTemporalInstant } = usePageSelector(selectCalendar)
  const { calendarApi } = useContext(FullCalendarApiContext)

  const timezoneHasTransition = useCallback(
    () =>
      startTemporalInstant
        .toZonedDateTimeISO(timezone)
        .getTimeZoneTransition("next") !== null,
    [timezone, startTemporalInstant],
  )

  const isCalendarInDST = useCallback(
    () =>
      startTemporalInstant.toZonedDateTimeISO(timezone).offsetNanoseconds >
      (startTemporalInstant
        .toZonedDateTimeISO(timezone)
        .getTimeZoneTransition("next")?.offsetNanoseconds || 0),
    [timezone, startTemporalInstant],
  )

  const handleCheckedChange = useCallback(() => {
    dispatch(updateTimetableTimezoneOffset(calendarApi!))
  }, [calendarApi, dispatch])

  return (
    <div
      className={cn("flex items-center space-x-2", {
        invisible: !timezoneHasTransition(),
      })}
    >
      <Switch
        id="daylight-savings-time"
        checked={isCalendarInDST()}
        onCheckedChange={handleCheckedChange}
      />
      <Label htmlFor="daylight-savings-time">Daylight Savings Time</Label>
    </div>
  )
}
